// src/components/ProtectedRoute/Admin/Consoles/Plugins/countries.plugin.js
import { Chip, Stack, Typography } from "@mui/material";
import supabase from "../../../../../utils/supabase";

const statusColor = {
  canonical: "success",
  synonym: "default",
  historical: "warning",
  pending: "default",
};

let altNamesSupported = true;
let geojsonNameSupported = true;

const buildSelect = () => {
  const base = [
    "id",
    "name",
    "status",
    "iso_a3",
    "parent_id",
    "period_from",
    "period_to",
    "note",
    "created_at",
    "updated_at",
  ];
  if (geojsonNameSupported) base.push("geojson_name");
  if (altNamesSupported) base.push("alt_names");
  base.push("parent:parent_id(id,name,status)");
  return base.join(",");
};

const plugin = {
  table: "countries",
  defaultSort: { field: "name", sort: "asc" },
  async fetch({ page, pageSize, orderBy, ascending, search }) {
    const orderColumn = ["name", "iso_a3", "status", "updated_at"].includes(orderBy) ? orderBy : "name";
    const from = page * pageSize;
    const to = from + pageSize - 1;

    for (let attempt = 0; attempt < 3; attempt++) {
      const selectStr = buildSelect();
      let query = supabase.from("countries").select(selectStr, { count: "exact" }).range(from, to);

      if (search) {
        const like = `%${search}%`;
        const clauses = [
          `name.ilike.${like}`,
          `iso_a3.ilike.${like}`,
        ];
        if (geojsonNameSupported) clauses.push(`geojson_name.ilike.${like}`);
        if (altNamesSupported) {
          const escaped = search.replace(/"/g, '\\"');
          clauses.push(`alt_names.cs.{\"${escaped}\"}`);
        }
        query = query.or(clauses.join(","));
      }

      query = query.order(orderColumn, { ascending });

      const { data, error, count } = await query;
      if (error) {
        const msg = error?.message || "";
        if (msg.includes("alt_names")) {
          altNamesSupported = false;
          continue;
        }
        if (msg.includes("geojson_name")) {
          geojsonNameSupported = false;
          continue;
        }
        console.error("[countries.plugin] fetch error", error);
        return { rows: [], total: 0 };
      }

      const rows = Array.isArray(data) ? data : [];
      const mapped = rows.map((row) => {
        const altNames = Array.isArray(row?.alt_names)
          ? row.alt_names
          : row?.alt_names
            ? [row.alt_names].flat().filter(Boolean)
            : [];
        return {
          ...row,
          alt_names: altNames,
          parent_name: row?.parent?.name || "",
        };
      });
      return { rows: mapped, total: typeof count === "number" ? count : mapped.length };
    }
    // Fallback if both attempts failed
    return { rows: [], total: 0 };
  },
  normalize(row) {
    return {
      ...row,
      parent_name: row?.parent?.name || "",
      alt_names: Array.isArray(row?.alt_names)
        ? row.alt_names
        : row?.alt_names
          ? [row.alt_names].flat().filter(Boolean)
          : [],
    };
  },
  columns() {
    const cols = [
      { field: "name", headerName: "Name", flex: 1, minWidth: 180 },
    ];
    if (geojsonNameSupported) {
      cols.push({ field: "geojson_name", headerName: "GeoJSON Name", flex: 1, minWidth: 160 });
    }
    cols.push(
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (p) => (
          <Chip
            size="small"
            label={p.row.status || "pending"}
            color={statusColor[p.row.status] || "default"}
            variant="outlined"
          />
        ),
      },
      { field: "parent_name", headerName: "Parent (canonical)", flex: 1, minWidth: 180 },
      { field: "iso_a3", headerName: "ISO A3", width: 100 },
      {
        field: "period",
        headerName: "Period",
        width: 160,
        valueGetter: (p) => {
          const row = p?.row || {};
          const from = row.period_from || "";
          const to = row.period_to || "";
          if (!from && !to) return "";
          return `${from || ""}${from || to ? " - " : ""}${to || ""}`;
        },
      },
      { field: "note", headerName: "Note", flex: 1, minWidth: 200 }
    );
    if (altNamesSupported) {
      cols.push({
        field: "alt_names",
        headerName: "Alt names",
        flex: 1,
        minWidth: 200,
        renderCell: (p) => {
          const arr = Array.isArray(p?.row?.alt_names) ? p.row.alt_names : [];
          if (!arr.length) return <Typography variant="body2">—</Typography>;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {arr.map((a, idx) => (
                <Chip key={`${a}-${idx}`} size="small" label={a} variant="outlined" />
              ))}
            </Stack>
          );
        },
      });
    }
    return cols;
  },
};

export default plugin;
